import { useState, useRef, useEffect, useCallback } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { fetchTags } from '@/lib/api'

interface TagInputProps {
  tags: string[]
  onTagsChange: (tags: string[]) => void
  placeholder?: string
  className?: string
}

export function TagInput({
  tags,
  onTagsChange,
  placeholder = 'Add tags...',
  className,
}: TagInputProps) {
  const [inputValue, setInputValue] = useState('')
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const inputRef = useRef<HTMLInputElement>(null)
  const suggestionsRef = useRef<HTMLDivElement>(null)

  // Fetch suggestions when input changes
  useEffect(() => {
    const searchTerm = inputValue.startsWith('#') ? inputValue.slice(1) : inputValue

    if (searchTerm.length === 0) {
      // Fetch popular tags when empty
      fetchTags()
        .then((results) => {
          // Filter out already selected tags
          const filtered = results.filter((t) => !tags.includes(t))
          setSuggestions(filtered.slice(0, 8))
        })
        .catch(() => setSuggestions([]))
      return
    }

    // Debounce the search
    const timer = setTimeout(() => {
      fetchTags(searchTerm)
        .then((results) => {
          // Filter out already selected tags
          const filtered = results.filter((t) => !tags.includes(t))
          setSuggestions(filtered.slice(0, 8))
        })
        .catch(() => setSuggestions([]))
    }, 150)

    return () => clearTimeout(timer)
  }, [inputValue, tags])

  const addTag = useCallback(
    (tag: string) => {
      const cleanTag = tag.startsWith('#') ? tag.slice(1) : tag
      const normalizedTag = cleanTag.toLowerCase().trim()

      if (normalizedTag && !tags.includes(normalizedTag)) {
        onTagsChange([...tags, normalizedTag])
      }
      setInputValue('')
      setSelectedIndex(-1)
      inputRef.current?.focus()
    },
    [tags, onTagsChange]
  )

  const removeTag = useCallback(
    (tagToRemove: string) => {
      onTagsChange(tags.filter((t) => t !== tagToRemove))
    },
    [tags, onTagsChange]
  )

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === 'Tab' || e.key === ',') {
      e.preventDefault()
      if (selectedIndex >= 0 && suggestions[selectedIndex]) {
        addTag(suggestions[selectedIndex])
      } else if (inputValue.trim()) {
        addTag(inputValue)
      }
    } else if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
      removeTag(tags[tags.length - 1])
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex((prev) =>
        prev < suggestions.length - 1 ? prev + 1 : prev
      )
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1))
    } else if (e.key === 'Escape') {
      setShowSuggestions(false)
      setSelectedIndex(-1)
    }
  }

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setShowSuggestions(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div className={cn('relative', className)}>
      <div className="flex flex-wrap items-center gap-1.5">
        {/* Existing tags */}
        {tags.map((tag) => (
          <span
            key={tag}
            className={cn(
              'inline-flex items-center gap-1 px-2 py-0.5',
              'text-xs font-light rounded-full',
              'bg-muted text-muted-foreground',
              'border border-border'
            )}
          >
            #{tag}
            <button
              type="button"
              onClick={() => removeTag(tag)}
              className="hover:text-foreground transition-colors"
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}

        {/* Input */}
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value)
            setShowSuggestions(true)
            setSelectedIndex(-1)
          }}
          onFocus={() => setShowSuggestions(true)}
          onKeyDown={handleKeyDown}
          placeholder={tags.length === 0 ? placeholder : ''}
          className={cn(
            'flex-1 min-w-[80px] bg-transparent border-none outline-none',
            'text-sm font-light text-foreground',
            'placeholder:text-muted-foreground'
          )}
        />
      </div>

      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className={cn(
            'absolute z-50 mt-1 w-full max-w-xs',
            'bg-background border border-border rounded-md shadow-lg',
            'py-1 max-h-48 overflow-auto'
          )}
        >
          {suggestions.map((suggestion, index) => (
            <button
              key={suggestion}
              type="button"
              onClick={() => addTag(suggestion)}
              className={cn(
                'w-full px-3 py-1.5 text-left text-sm font-light',
                'hover:bg-muted transition-colors',
                index === selectedIndex && 'bg-muted'
              )}
            >
              #{suggestion}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
